import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  ComposedChart, Line, Area, Bar, XAxis, YAxis, CartesianGrid, 
  Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend, Brush 
} from 'recharts';
import { 
  ShieldCheck, AlertTriangle, Calendar, Filter, Download, 
  Search, ArrowLeft, Package, Activity, Zap, Layers,
  ChevronRight, RefreshCw, Thermometer, Box
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

// --- Advanced Theme Configuration ---
const THEME = {
  blue: { gradient: ['#0ea5e9', '#2563eb'], shadow: 'shadow-blue-100' },
  teal: { gradient: ['#14b8a6', '#0d9488'], shadow: 'shadow-teal-100' },
  purple: { gradient: ['#a855f7', '#7c3aed'], shadow: 'shadow-purple-100' },
  rose: { gradient: ['#f43f5e', '#e11d48'], shadow: 'shadow-rose-100' },
  amber: { gradient: ['#f59e0b', '#d97706'], shadow: 'shadow-amber-100' },
};

// --- Mock Data ---
const lifecycleTrend = [
  { date: '2025-01', maintenance: 45, replacements: 12, health: 88 },
  { date: '2025-02', maintenance: 52, replacements: 8, health: 85 },
  { date: '2025-03', maintenance: 48, replacements: 15, health: 82 },
  { date: '2025-04', maintenance: 61, replacements: 10, health: 90 },
  { date: '2025-05', maintenance: 55, replacements: 18, health: 78 },
  { date: '2025-06', maintenance: 67, replacements: 12, health: 84 },
];

const itemsData = [
  { id: 'ASSET-99', name: 'Precision CNC 5-Axis', category: 'Heavy Machinery', sn: 'XC-9001', expiry: '2026-11-20', stage: 'Optimal', risk: 'Low', health: 94 },
  { id: 'ASSET-42', name: 'Smart Relay Router', category: 'Networking', sn: 'NW-1102', expiry: '2026-02-14', stage: 'Legacy', risk: 'High', health: 42 },
  { id: 'ASSET-15', name: 'Hydraulic Press v2', category: 'Heavy Machinery', sn: 'HP-4451', expiry: '2025-10-05', stage: 'Maintenance', risk: 'Medium', health: 68 },
];

// --- High-End Components ---

const GlassCard = ({ children, className }) => (
  <div className={`bg-white/70 backdrop-blur-md border border-white/20 rounded-2xl ${className}`}>
    {children}
  </div>
);

const AdvancedMetric = ({ label, value, trend, icon: Icon, color }) => (
  <motion.div 
    whileHover={{ y: -5 }}
    className="flex flex-col p-5 border-r border-gray-100 last:border-r-0 min-w-[200px]"
  >
    <div className="flex items-center justify-between mb-3">
      <div className={`p-2 rounded-xl bg-gradient-to-br from-${color}-50 to-${color}-100 text-${color}-600`}>
        <Icon size={20} />
      </div>
      <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full bg-${trend > 0 ? 'emerald' : 'rose'}-50 text-${trend > 0 ? 'emerald' : 'rose'}-600`}>
        {trend > 0 ? '+' : ''}{trend}%
      </span>
    </div>
    <span className="text-[10px] font-extrabold text-gray-400 uppercase tracking-[0.15em] font-['Poppins']">{label}</span>
    <h3 className="text-2xl font-bold text-gray-900 mt-1 font-['Poppins'] tracking-tight">{value}</h3>
  </motion.div>
);

const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-slate-900 text-white p-3 rounded-xl shadow-2xl border border-slate-800 text-[11px]">
        <p className="font-bold mb-2 border-b border-slate-700 pb-1">{label}</p>
        {payload.map((entry, index) => (
          <div key={index} className="flex items-center gap-2 py-0.5">
            <div className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: entry.color }} />
            <span className="capitalize">{entry.name}:</span>
            <span className="font-mono font-bold">{entry.value}</span>
          </div>
        ))}
      </div>
    );
  }
  return null;
};

// --- Main Screen ---

export default function AdvancedItemLifecycle() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('Overview');
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    const timer = setTimeout(() => setLoading(false), 1500);
    return () => clearTimeout(timer);
  }, []);

  const filteredItems = useMemo(() => {
    return itemsData.filter(item => 
      item.name.toLowerCase().includes(searchTerm.toLowerCase()) || 
      item.sn.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [searchTerm]);

  return (
    <div className="min-h-screen bg-[#F8FAFC] p-4 lg:p-8 font-['Roboto'] text-slate-700">
      
      {/* Dynamic Header */}
      <header className="flex flex-col xl:flex-row justify-between items-start xl:items-center gap-6 mb-10">
        <div className="flex items-center gap-5">
          <motion.button 
            whileTap={{ scale: 0.9 }}
            onClick={() => navigate(-1)}
            className="p-3 bg-white shadow-sm rounded-2xl hover:shadow-md transition-all text-slate-400"
          >
            <ArrowLeft size={20} />
          </motion.button>
          <div>
            <div className="flex items-center gap-2 mb-1">
              <span className="p-1 bg-blue-500 rounded text-white"><Box size={12}/></span>
              <span className="text-[10px] font-black uppercase tracking-[0.2em] text-blue-500 font-['Poppins']">Asset Intelligence</span>
            </div>
            <h1 className="text-3xl font-black text-slate-900 font-['Poppins'] tracking-tight">Item Lifecycle & Warranty</h1>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-4">
          <div className="flex bg-white p-1.5 rounded-2xl shadow-sm border border-slate-100">
            {['Overview', 'Maintenance', 'Registry'].map(tab => (
              <button 
                key={tab} 
                onClick={() => setActiveTab(tab)}
                className={`px-6 py-2 text-xs font-bold font-['Poppins'] rounded-xl transition-all ${
                  activeTab === tab ? 'bg-slate-900 text-white shadow-lg' : 'text-slate-400 hover:text-slate-600'
                }`}
              >
                {tab}
              </button>
            ))}
          </div>
          <button className="flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-blue-600 to-blue-700 text-white text-xs font-black rounded-2xl hover:shadow-xl hover:shadow-blue-200 transition-all uppercase tracking-widest">
            <Download size={16} /> Export Intelligence
          </button>
        </div>
      </header>

      {/* Responsive Metrics Bar */}
      <GlassCard className="flex flex-wrap lg:flex-nowrap mb-8 overflow-hidden shadow-sm">
        <AdvancedMetric label="Total Active Fleet" value="2,840" trend={12} icon={Layers} color="blue" />
        <AdvancedMetric label="Warranty Compliance" value="98.2%" trend={4} icon={ShieldCheck} color="teal" />
        <AdvancedMetric label="Avg Health Score" value="76/100" trend={-2} icon={Activity} color="purple" />
        <AdvancedMetric label="Expiring (90 Days)" value="142" trend={18} icon={AlertTriangle} color="amber" />
      </GlassCard>

      <div className="grid grid-cols-12 gap-8">
        
        {/* Advanced Trend Chart */}
        <div className="col-span-12 lg:col-span-8">
          <div className="flex items-center justify-between mb-8">
            <h3 className="text-sm font-black font-['Poppins'] uppercase tracking-widest text-slate-400 flex items-center gap-3">
              <Zap size={18} className="text-amber-500 fill-amber-500" /> Operational Health vs Maintenance
            </h3>
            <div className="flex gap-4">
              <div className="flex items-center gap-2 text-[10px] font-bold text-slate-400">
                <div className="w-3 h-1 bg-blue-500 rounded-full" /> MAINT.
              </div>
              <div className="flex items-center gap-2 text-[10px] font-bold text-slate-400">
                <div className="w-3 h-1 bg-purple-500 rounded-full" /> HEALTH
              </div>
            </div>
          </div>
          <div className="h-[400px] w-full">
            {loading ? <div className="w-full h-full bg-slate-200/50 animate-pulse rounded-3xl" /> : (
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart data={lifecycleTrend}>
                  <defs>
                    <linearGradient id="areaGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.1}/>
                      <stop offset="95%" stopColor="#3b82f6" stopOpacity={0}/>
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E2E8F0" />
                  <XAxis dataKey="date" axisLine={false} tickLine={false} tick={{fontSize: 10, fontWeight: 700}} dy={10} />
                  <YAxis axisLine={false} tickLine={false} tick={{fontSize: 10, fontWeight: 700}} />
                  <Tooltip content={<CustomTooltip />} />
                  <Area type="monotone" dataKey="maintenance" fill="url(#areaGrad)" stroke="#3b82f6" strokeWidth={3} />
                  <Line type="stepAfter" dataKey="health" stroke="#a855f7" strokeWidth={4} dot={{ r: 6, fill: '#fff', stroke: '#a855f7', strokeWidth: 3 }} />
                  <Bar dataKey="replacements" fill="#f43f5e" radius={[4, 4, 0, 0]} barSize={20} />
                  <Brush dataKey="date" height={30} stroke="#E2E8F0" fill="#fff" />
                </ComposedChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>

        {/* Status Pie - Advanced Design */}
        <div className="col-span-12 lg:col-span-4">
          <h3 className="text-sm font-black font-['Poppins'] uppercase tracking-widest text-slate-400 mb-8">
            Warranty Exposure
          </h3>
          <div className="h-[300px] relative">
            {loading ? <div className="w-full h-full rounded-full border-[20px] border-slate-100 animate-spin" /> : (
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie 
                    data={[
                      { name: 'Secure', value: 70, color: '#10b981' },
                      { name: 'At Risk', value: 20, color: '#f59e0b' },
                      { name: 'Critical', value: 10, color: '#f43f5e' }
                    ]} 
                    innerRadius={80} 
                    outerRadius={110} 
                    paddingAngle={15} 
                    dataKey="value"
                    stroke="none"
                  >
                    {[0, 1, 2].map((entry, index) => (
                      <Cell key={index} fill={['#10b981', '#f59e0b', '#f43f5e'][index]} />
                    ))}
                  </Pie>
                  <Tooltip content={<CustomTooltip />} />
                </PieChart>
              </ResponsiveContainer>
            )}
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 text-center">
              <p className="text-[10px] font-black text-slate-400 uppercase">Coverage</p>
              <h4 className="text-3xl font-black text-slate-900">82%</h4>
            </div>
          </div>
          <div className="space-y-3 mt-6">
            {['Secure', 'At Risk', 'Critical'].map((s, i) => (
              <div key={s} className="flex justify-between items-center p-3 bg-white rounded-xl border border-slate-50 shadow-sm">
                <div className="flex items-center gap-3">
                  <div className={`w-2 h-2 rounded-full bg-${['emerald', 'amber', 'rose'][i]}-500`} />
                  <span className="text-xs font-bold text-slate-600">{s}</span>
                </div>
                <span className="text-xs font-black text-slate-900">{[70, 20, 10][i]}%</span>
              </div>
            ))}
          </div>
        </div>

        {/* Asset Intelligence Registry */}
        <div className="col-span-12 mt-8">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-8">
            <h3 className="text-sm font-black font-['Poppins'] uppercase tracking-widest text-slate-400">
              Fleet Lifecycle Registry
            </h3>
            <div className="relative w-full md:w-80">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-300" size={16} />
              <input 
                type="text" 
                placeholder="Search Asset Intelligence..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-12 pr-4 py-3 bg-white border border-slate-100 rounded-2xl text-xs font-bold focus:ring-4 focus:ring-blue-50 transition-all outline-none"
              />
            </div>
          </div>

          <div className="overflow-x-auto rounded-3xl border border-slate-100 bg-white">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="bg-slate-50/50">
                  <th className="p-6 text-[10px] font-black text-slate-400 uppercase tracking-widest">Asset Details</th>
                  <th className="p-6 text-[10px] font-black text-slate-400 uppercase tracking-widest">Health State</th>
                  <th className="p-6 text-[10px] font-black text-slate-400 uppercase tracking-widest text-center">Warranty End</th>
                  <th className="p-6 text-[10px] font-black text-slate-400 uppercase tracking-widest">Risk Level</th>
                  <th className="p-6 text-[10px] font-black text-slate-400 uppercase tracking-widest text-right">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-50">
                {filteredItems.map((item) => (
                  <motion.tr 
                    layout
                    key={item.id} 
                    className="hover:bg-blue-50/30 transition-all group"
                  >
                    <td className="p-6">
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-12 rounded-2xl bg-slate-100 flex items-center justify-center text-slate-400 group-hover:bg-blue-600 group-hover:text-white transition-all duration-300">
                          <Thermometer size={20} />
                        </div>
                        <div>
                          <p className="text-sm font-black text-slate-900 font-['Poppins']">{item.name}</p>
                          <div className="flex items-center gap-2 mt-1">
                             <span className="text-[9px] font-bold text-slate-400 uppercase tracking-tighter">{item.category}</span>
                             <span className="text-[9px] font-mono font-bold text-blue-500 bg-blue-50 px-1.5 rounded uppercase">{item.sn}</span>
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="p-6">
                      <div className="flex items-center gap-4">
                         <div className="flex-1 h-2 bg-slate-100 rounded-full overflow-hidden max-w-[100px]">
                            <motion.div 
                              initial={{ width: 0 }}
                              animate={{ width: `${item.health}%` }}
                              className={`h-full rounded-full ${item.health > 80 ? 'bg-emerald-500' : item.health > 50 ? 'bg-amber-500' : 'bg-rose-500'}`} 
                            />
                         </div>
                         <span className="text-xs font-black text-slate-700">{item.health}%</span>
                      </div>
                    </td>
                    <td className="p-6 text-center">
                      <span className={`text-[10px] font-black px-3 py-1.5 rounded-xl border ${
                        item.risk === 'High' ? 'bg-rose-50 text-rose-600 border-rose-100' : 'bg-slate-50 text-slate-600 border-slate-100'
                      }`}>
                        {item.expiry}
                      </span>
                    </td>
                    <td className="p-6">
                       <span className={`flex items-center gap-1.5 text-[10px] font-black uppercase ${
                         item.risk === 'High' ? 'text-rose-500' : item.risk === 'Medium' ? 'text-amber-500' : 'text-emerald-500'
                       }`}>
                         <div className={`w-1.5 h-1.5 rounded-full bg-current ${item.risk === 'High' ? 'animate-pulse' : ''}`} />
                         {item.risk} Risk
                       </span>
                    </td>
                    <td className="p-6 text-right">
                       <button className="p-2.5 text-slate-300 hover:text-slate-900 hover:bg-slate-100 rounded-xl transition-all">
                         <ChevronRight size={20} />
                       </button>
                    </td>
                  </motion.tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Advanced Global Refresh Sync */}
      <AnimatePresence>
        {loading && (
          <motion.div 
            initial={{ opacity: 0, y: 100 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9 }}
            className="fixed bottom-8 left-1/2 -translate-x-1/2 z-50 px-8 py-4 bg-slate-900 shadow-2xl rounded-3xl flex items-center gap-4"
          >
            <RefreshCw className="text-blue-400 animate-spin" size={20} />
            <div className="flex flex-col">
               <span className="text-[10px] font-black text-white uppercase tracking-widest font-['Poppins']">Synchronizing Neural Data</span>
               <span className="text-[9px] text-slate-400 font-bold uppercase">Real-time assets stream active...</span>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

    </div>
  );
}